import { useState } from 'react'
import { motion } from 'framer-motion'
import { Shield, ChevronDown, ChevronUp, Save, AlertCircle, CheckCircle, Eye, EyeOff, Fingerprint, Smartphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import toast from 'react-hot-toast'

interface SecuritySectionProps {
  title: string
  subtitle: string
}

export function SecuritySection({ title, subtitle }: SecuritySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Password change form
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  })
  
  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    two_factor_enabled: false,
    biometric_enabled: false
  })
  
  const { t } = useTranslation()

  // Load security settings when expanding
  const handleExpand = async () => {
    if (!isExpanded) {
      setIsLoading(true)
      setError(null)
      setSuccess(null)
      
      try {
        // Load current security settings from API
        const response = await fetch('/api/security-settings/', {
          credentials: 'include'
        })
        const data = await response.json()
        
        if (data.results && data.results.length > 0) {
          const settings = data.results[0]
          setSecuritySettings({
            two_factor_enabled: settings.two_factor_enabled,
            biometric_enabled: settings.biometric_enabled
          })
        }
      } catch (error: any) {
        console.error('Error loading security settings:', error)
        setError(t('settings.securityLoadError', 'Failed to load security settings'))
      } finally {
        setIsLoading(false)
      }
    }
    setIsExpanded(!isExpanded)
  }

  // Handle password form input changes
  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }))
    if (error) setError(null)
    if (success) setSuccess(null)
  }

  // Change password
  const handlePasswordSave = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError(t('settings.passwordMismatch', 'New passwords do not match'))
      return
    }

    if (passwordData.new_password.length < 8) {
      setError(t('settings.passwordTooShort', 'Password must be at least 8 characters'))
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetch('/api/security-settings/change_password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(t('settings.passwordChangedSuccess', 'Password changed successfully!'))
        toast.success(t('settings.passwordChangedSuccess', 'Password changed successfully!'))
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' })
      } else {
        setError(data.error || t('settings.passwordChangeError', 'Failed to change password'))
      }
    } catch (err: any) {
      console.error('Error changing password:', err)
      setError(t('settings.passwordChangeError', 'Failed to change password'))
    } finally {
      setIsLoading(false)
    }
  }

  // Toggle 2FA
  const handleToggle2FA = async () => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetch('/api/security-settings/toggle_2fa/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          enabled: !securitySettings.two_factor_enabled
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSecuritySettings(prev => ({
          ...prev,
          two_factor_enabled: data.two_factor_enabled
        }))
        setSuccess(data.message)
        toast.success(data.message)
      } else {
        setError(data.error || t('settings.toggle2FAError', 'Failed to toggle 2FA'))
      }
    } catch (err: any) {
      console.error('Error toggling 2FA:', err)
      setError(t('settings.toggle2FAError', 'Failed to toggle 2FA'))
    } finally {
      setIsLoading(false)
    }
  }

  // Toggle biometric
  const handleToggleBiometric = async () => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetch('/api/security-settings/toggle_biometric/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          enabled: !securitySettings.biometric_enabled
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSecuritySettings(prev => ({
          ...prev,
          biometric_enabled: data.biometric_enabled
        }))
        setSuccess(data.message)
        toast.success(data.message)
      } else {
        setError(data.error || t('settings.toggleBiometricError', 'Failed to toggle biometric authentication'))
      }
    } catch (err: any) {
      console.error('Error toggling biometric:', err)
      setError(t('settings.toggleBiometricError', 'Failed to toggle biometric authentication'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <motion.button
        onClick={handleExpand}
        className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-medium text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary">{subtitle}</p>
        </div>
        <div className="text-text-secondary">
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : isExpanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
      </motion.button>
      
      {/* Security Settings Dropdown */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-100 bg-gray-50"
        >
          <div className="p-4 space-y-6">
            {/* Success Message */}
            {success && (
              <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Change Password Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary">
                {t('settings.changePassword', 'Change Password')}
              </h4>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-primary">
                    {t('settings.currentPassword', 'Current Password')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.old ? 'text' : 'password'}
                      value={passwordData.old_password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange('old_password', e.target.value)}
                      placeholder={t('settings.currentPasswordPlaceholder', 'Enter current password')}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.old ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-primary">
                    {t('settings.newPassword', 'New Password')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange('new_password', e.target.value)}
                      placeholder={t('settings.newPasswordPlaceholder', 'Enter new password')}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-primary">
                    {t('settings.confirmPassword', 'Confirm Password')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange('confirm_password', e.target.value)}
                      placeholder={t('settings.confirmPasswordPlaceholder', 'Confirm new password')}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handlePasswordSave}
                  disabled={isLoading || !passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password}
                  className="px-6"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('common.saving', 'Saving...')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="h-4 w-4" />
                      <span>{t('settings.changePassword', 'Change Password')}</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>

            {/* Security Features Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary">
                {t('settings.securityFeatures', 'Security Features')}
              </h4>
              
              {/* 2FA Toggle */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Smartphone className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.twoFactorAuth', 'Two-Factor Authentication')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.twoFactorDesc', 'Add an extra layer of security')}</p>
                  </div>
                </div>
                <button
                  onClick={handleToggle2FA}
                  disabled={isLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    securitySettings.two_factor_enabled ? 'bg-primary' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      securitySettings.two_factor_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Biometric Toggle */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Fingerprint className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-text-primary">{t('settings.biometricAuth', 'Biometric Authentication')}</h5>
                    <p className="text-sm text-text-secondary">{t('settings.biometricDesc', 'Use fingerprint or face recognition')}</p>
                  </div>
                </div>
                <button
                  onClick={handleToggleBiometric}
                  disabled={isLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    securitySettings.biometric_enabled ? 'bg-primary' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      securitySettings.biometric_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}
